import { useState } from 'react';
import { AxiosError } from 'axios';
import { DsButton, DsInput, DsModal } from '@/modules/internal-v2/ds';
import { useRoles } from '@/api/roles/roles.hooks';
import { useUpdateUserAccess } from '@/api/user/user.hooks';
import { UserDetail } from '@/api/user/user.types';
import { ACCOUNT_TYPE, AccountType } from '@/constants/account-type';
import { SalesPersonPicker } from './sales-person-picker.component';

interface UserAccessEditorProps {
  user: UserDetail;
  onClose: () => void;
}

export function UserAccessEditor({ user, onClose }: UserAccessEditorProps) {
  const { data: roles } = useRoles();
  const updateAccess = useUpdateUserAccess();
  const isAdminAccount = user.isAdmin;

  const [accountType, setAccountType] = useState<AccountType>(user.accountType);
  const [roleIds, setRoleIds] = useState<Set<string>>(
    new Set(user.assignedRoles.map((role) => role.id)),
  );
  const [salesPersonCode, setSalesPersonCode] = useState<number | null>(
    user.salesPersonCode ?? null,
  );
  const [businessPartnerIds, setBusinessPartnerIds] = useState<number[]>(
    user.assignedBusinessPartnerIds,
  );
  const [bpInput, setBpInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const toggleRole = (id: string) => {
    setRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const addBusinessPartner = () => {
    const id = Number.parseInt(bpInput.trim(), 10);
    if (!Number.isInteger(id) || id <= 0) {
      return;
    }
    setBusinessPartnerIds((prev) =>
      prev.includes(id) ? prev : [...prev, id].sort((a, b) => a - b),
    );
    setBpInput('');
  };

  const removeBusinessPartner = (id: number) => {
    setBusinessPartnerIds((prev) => prev.filter((value) => value !== id));
  };

  const handleSave = () => {
    setError(null);
    // EXTERNAL accounts can't carry an internal role assignment or a sales-
    // person code — clear both when demoting before the request reaches the
    // backend, which also rejects the combination.
    const effectiveRoleIds =
      accountType === ACCOUNT_TYPE.EXTERNAL ? [] : [...roleIds];
    const effectiveSalesPersonCode =
      accountType === ACCOUNT_TYPE.EXTERNAL ? null : salesPersonCode;

    updateAccess.mutate(
      {
        userId: user.id,
        body: {
          accountType,
          roleIds: effectiveRoleIds,
          salesPersonCode: effectiveSalesPersonCode,
          businessPartnerIds,
        },
      },
      {
        onSuccess: onClose,
        onError: (e: AxiosError) =>
          setError(
            (e.response?.data as { message?: string })?.message ??
              'Speichern fehlgeschlagen.',
          ),
      },
    );
  };

  return (
    <DsModal
      open
      onClose={onClose}
      title={user.email}
      description="Rollen und Geschäftspartner-Zugriff verwalten"
      footer={
        <>
          <DsButton onClick={onClose}>Abbrechen</DsButton>
          <DsButton
            variant="primary"
            disabled={updateAccess.isPending}
            onClick={handleSave}
          >
            {updateAccess.isPending ? 'Speichert…' : 'Speichern'}
          </DsButton>
        </>
      }
    >
      <div className="user-form">
        {error && <div className="ds-alert neg">{error}</div>}

        {isAdminAccount && (
          <div className="ds-alert">
            Dieser Benutzer ist ein Administrator und hat unabhängig von den
            zugewiesenen Rollen vollen Zugriff.
          </div>
        )}

        <div className="user-field">
          <span className="user-field-label">Kontotyp</span>
          <div className="user-roles">
            <label className="user-role-item">
              <input
                type="radio"
                name="account-type"
                checked={accountType === ACCOUNT_TYPE.INTERNAL}
                onChange={() => setAccountType(ACCOUNT_TYPE.INTERNAL)}
              />
              <span className="user-role-text">
                <span className="user-role-name">Intern (Mitarbeiter)</span>
                <span className="user-role-meta">
                  Voller Zugriff auf den internen Bereich; Rollen + SAP-Code
                  bestimmen die Berechtigungen.
                </span>
              </span>
            </label>
            <label className="user-role-item">
              <input
                type="radio"
                name="account-type"
                checked={accountType === ACCOUNT_TYPE.EXTERNAL}
                onChange={() => setAccountType(ACCOUNT_TYPE.EXTERNAL)}
              />
              <span className="user-role-text">
                <span className="user-role-name">Extern (Kunde)</span>
                <span className="user-role-meta">
                  Nur Kundenbereich; keine Rollen oder SAP-Code zuweisbar.
                </span>
              </span>
            </label>
          </div>
        </div>

        <div className="user-field">
          <span className="user-field-label">Rollen</span>
          <div className="user-roles">
            {accountType === ACCOUNT_TYPE.EXTERNAL && (
              <span className="user-muted">
                Externe Konten können keine Rollen tragen.
              </span>
            )}
            {accountType === ACCOUNT_TYPE.INTERNAL &&
              (roles ?? []).length === 0 && (
                <span className="user-muted">
                  Es sind noch keine Rollen angelegt.
                </span>
              )}
            {accountType === ACCOUNT_TYPE.INTERNAL &&
              (roles ?? []).map((role) => (
                <label key={role.id} className="user-role-item">
                  <input
                    type="checkbox"
                    checked={roleIds.has(role.id)}
                    onChange={() => toggleRole(role.id)}
                  />
                  <span className="user-role-text">
                    <span className="user-role-name">{role.name}</span>
                    <span className="user-role-meta">
                      {role.businessPartnerScope === 'ALL'
                        ? 'Alle Geschäftspartner'
                        : 'Eigene Geschäftspartner'}{' '}
                      · {role.permissions.length} Berechtigungen
                    </span>
                  </span>
                </label>
              ))}
          </div>
        </div>

        {accountType === ACCOUNT_TYPE.INTERNAL && (
          <div className="user-field">
            <span className="user-field-label">SAP-Verkäufer</span>
            <SalesPersonPicker
              value={salesPersonCode}
              onChange={setSalesPersonCode}
            />
            <span className="user-role-meta">
              Bestimmt die Geschäftspartner, die dieser Mitarbeiter als
              „eigene" sieht. Suche nach Name, E-Mail oder SAP-Code.
            </span>
          </div>
        )}

        <div className="user-field">
          <span className="user-field-label">
            Zusätzliche Geschäftspartner (manuell)
          </span>
          <div className="user-bp-add">
            <DsInput
              value={bpInput}
              inputMode="numeric"
              placeholder="Geschäftspartner-ID"
              onChange={(e) => setBpInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addBusinessPartner();
                }
              }}
            />
            <DsButton onClick={addBusinessPartner}>Hinzufügen</DsButton>
          </div>
          <div className="user-bp-chips">
            {businessPartnerIds.length === 0 && (
              <span className="user-muted">
                Keine manuell zugewiesenen Geschäftspartner.
              </span>
            )}
            {businessPartnerIds.map((id) => (
              <span key={id} className="user-bp-chip">
                {id}
                <button
                  type="button"
                  aria-label={`Geschäftspartner ${id} entfernen`}
                  onClick={() => removeBusinessPartner(id)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </DsModal>
  );
}
